import * as React from "react";
import { useParams } from "react-router";
import { OfficeDocumentView } from "../components/documents/OfficeDocumentView";
import { documentService } from "../services/documentService";
import { OfficeOpenDialog } from "../components/documents/OfficeOpenDialog";
import { useOfficeResume } from "../components/documents/useOfficeResume";

/**
 * Word/Excel/PowerPoint auf der ganzen Seite — eigener Tab, kein App-Rahmen.
 *
 * Bis dahin öffnete der Editor im Fenster-Manager: links die Navigationsleiste,
 * oben die Kopfzeile, und darunter ein Editor, der seinerseits eine
 * Werkzeugleiste mitbringt. Word im Browser ist aber selbst eine Anwendung und
 * kein Karteireiter — auf einem 13-Zoll-Bildschirm blieb für das Dokument
 * kaum Platz.
 *
 * Diese Route hängt deshalb bewusst NICHT unter `<AppShell />` (siehe
 * `App.tsx`): dort wohnen Navigationsleiste und Kopfzeile. Ein Pfad davor
 * bekommt die nackte Fläche — denselben Weg gehen schon Login und die
 * öffentlichen Formulare.
 *
 * Aufgerufen wird sie über `window.open` aus `useOpenDocument`, damit die
 * eigentliche Anwendung im ursprünglichen Tab stehen bleibt: Wer am Vorgang
 * arbeitet, will nach dem Schließen des Dokuments wieder dort sein, wo er war.
 */
export const OfficeEditorPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const docId = Number(id);
    const [fileName, setFileName] = React.useState<string>("");
    const [fehler, setFehler] = React.useState<string | null>(null);
    const resumed = useOfficeResume();
    const [resumeDone, setResumeDone] = React.useState(false);

    React.useEffect(() => {
        if (!Number.isFinite(docId) || docId <= 0) {
            setFehler("Diese Adresse führt zu keinem Dokument.");
            return;
        }
        let aktiv = true;
        documentService
            .getOne(docId)
            .then((doc) => {
                if (!aktiv) return;
                setFileName(doc.fileName);
                // Der Tab-Titel ist die einzige Beschriftung, die bleibt — der
                // Editor selbst zeigt den Dateinamen nur in seiner eigenen Leiste.
                document.title = `${doc.fileName} — KMVS`;
            })
            .catch(() => {
                if (!aktiv) return;
                // 404 heißt hier auch „anderer Mandant" — der Grund gehört dem
                // Server, nicht dieser Seite.
                setFehler("Das Dokument ist nicht (mehr) verfügbar.");
            });
        return () => {
            aktiv = false;
        };
    }, [docId]);

    if (resumed) {
        if ("error" in resumed) return <p role="alert" style={{ padding: 24 }}>{resumed.error}</p>;
        if (resumeDone) return <p style={{ padding: 24 }}>Dokument geöffnet. <a href="/">Zurück zu KMVS</a></p>;
        return <OfficeOpenDialog presentation="popover" documentId={resumed.intent.documentId}
            resumeTarget={resumed.intent.target} resumeInOwnTab fileName={fileName}
            onOpened={() => {}} onDismiss={() => setResumeDone(true)} />;
    }
    if (fehler) {
        return (
            <div
                style={{
                    height: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "24px",
                    fontFamily: "system-ui, sans-serif",
                    color: "#5B6570",
                    textAlign: "center",
                }}
            >
                {fehler}
            </div>
        );
    }

    // Erst rendern, wenn der Dateiname da ist: OfficeDocumentView baut den
    // Editor im Effekt auf, und ein Neuaufbau wegen eines nachgereichten Props
    // wäre für den Nutzer ein sichtbares Flackern.
    if (!fileName) {
        return <div style={{ height: "100vh" }} />;
    }

    return (
        <div style={{ height: "100vh", overflow: "hidden" }}>
            <OfficeDocumentView docId={docId} fileName={fileName} />
        </div>
    );
};

export default OfficeEditorPage;
